import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getAdoptionOverview } from '../../lib/api';
import { TrendUp, TrendDown } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RANGES = [
  { value: 'this_week', label: 'This Week' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
];

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#a855f7', '#ec4899', '#525252', '#0ea5e9', '#f43f5e'];

export default function FeatureUsage() {
  const [range, setRange] = useState('last_30_days');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFeature, setActiveFeature] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await getAdoptionOverview(range)); }
    catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const { feature_usage, rows } = data;
  const total = feature_usage.reduce((s, f) => s + f.count, 0);

  // Users behind a feature
  const usersForFeature = (key) => rows.filter(r => r.feature_counts && r.feature_counts[key] > 0)
    .map(r => ({ ...r, feature_uses: r.feature_counts[key] }))
    .sort((a, b) => b.feature_uses - a.feature_uses);

  return (
    <div className="space-y-4" data-testid="feature-usage">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-foreground">Feature Usage</h2>
          <p className="text-xs text-muted-foreground mt-1">What the team is using Clara for. Click a feature to see who&apos;s driving it.</p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40 h-9 border-border text-xs" data-testid="feature-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-card border-border lg:col-span-2">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">Breakdown</h3>
            {feature_usage.length === 0 ? (
              <p className="text-xs text-muted-foreground py-8 text-center">No usage in this period.</p>
            ) : (
              <div className="space-y-2">
                {feature_usage.map((f, i) => {
                  const pct = total ? Math.round((f.count / total) * 100) : 0;
                  const color = COLORS[i % COLORS.length];
                  return (
                    <button key={f.key} onClick={() => setActiveFeature(activeFeature === f.key ? null : f.key)}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${activeFeature === f.key ? 'border-[#E63935]/40 bg-[#E63935]/5' : 'border-border hover:bg-muted/20'}`}
                      data-testid={`feature-${f.key}`}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
                          <span className="text-sm font-semibold text-foreground">{f.label}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{f.count} uses</span>
                          <span className="text-sm font-bold text-foreground w-10 text-right">{pct}%</span>
                          {f.trend_pct !== 0 && (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${f.trend_pct > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {f.trend_pct > 0 ? <TrendUp size={11} /> : <TrendDown size={11} />}
                              {f.trend_pct > 0 ? '+' : ''}{f.trend_pct}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">
              {activeFeature ? `Who's using ${feature_usage.find(f => f.key === activeFeature)?.label}` : 'Click a feature to see users'}
            </h3>
            {activeFeature ? (
              <div className="space-y-1.5">
                {usersForFeature(activeFeature).slice(0, 15).map(u => (
                  <div key={u.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-md bg-[#E63935]/20 text-[#E63935] flex items-center justify-center text-[10px] font-bold">{u.initials}</span>
                      <span className="text-xs text-foreground">{u.name}</span>
                    </div>
                    <span className="text-xs font-bold text-foreground">{u.feature_uses}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Selecting a feature drills into the team members who use it most.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
